import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from '../../../RouterCompatibility';
import PropTypes from 'prop-types';
import CustomModal from '../../../Modal';
import CustomTooltip from '../../../Tooltip';
import DataComponent from '../../../DataComponent/DataComponent';
import TreeGoalActions from '../../../TreeGoalActions/TreeGoalActions';
import runtimeConfig from '../../../../config/runtimeConfig';
import { getConversionStatuses } from '../../../../helpers/common';
import { getPath } from '../../../../utils/Router';
import { escape } from '../../../../helpers/escape';
import {isMobile} from '../../../../utils/DeviceDetect';
import { withTranslation } from '../../../ChildrenContext';

const ListPostbacks = ({
    list: propList,
    page: propPage,
    total: propTotal,
    loading: propLoading,
    headerNames: propHeaderNames,
    goalActions: propGoalActions,
    getListPostbacks,
    getListGoalActionsByIds,
    onDeletePostback,
    t
}) => {
    const staticNames = t?.postbackPage || {};
    const staticNamesStatuses = t?.conversionStatuses || {};
    const staticNamesActions = t?.actions || {};

    const isMobileDevice = isMobile.any();
    const hasLoadedRef = useRef(false);
    const getListPostbacksRef = useRef(getListPostbacks);

    useEffect(() => {
        getListPostbacksRef.current = getListPostbacks;
    }, [getListPostbacks]);

    const [state, setState] = useState({
        headerNames: propHeaderNames,
        list: propList,
        page: propPage,
        totalPages: propTotal,
        loading: propLoading,
        deletePostbackId: null,
        deletePostbackTitle: '',
        deleteModalIsVisible: false
    });

    useEffect(() => {
        getListPostbacksRef.current();

        return () => {
            hasLoadedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (propLoading === false && propList !== undefined) {
            hasLoadedRef.current = true;
        }
    }, [propList, propLoading]);

    useEffect(() => {
        setState(prevState => {
            if (
                propHeaderNames !== prevState.headerNames ||
                propList !== prevState.list ||
                propPage !== prevState.page ||
                propTotal !== prevState.totalPages ||
                propLoading !== prevState.loading
            ) {
                return {
                    ...prevState,
                    headerNames: propHeaderNames,
                    list: propList,
                    page: propPage,
                    totalPages: propTotal,
                    loading: propLoading
                };
            }
            return prevState;
        });
    }, [propHeaderNames, propList, propPage, propTotal, propLoading]);

    const onDataTableAction = useCallback((state, actionType) => {
        if (actionType === 'firstRender') {
            return;
        }
        getListPostbacks(state);
    }, [getListPostbacks]);

    const onDeletePostbackHandler = useCallback(() => {
        onDeletePostback(state);

        setState(prevState => ({
            ...prevState,
            deleteModalIsVisible: false
        }));
    }, [onDeletePostback, state]);

    const showDeleteModal = useCallback((id, title) => {
        setState(prevState => ({
            ...prevState,
            deletePostbackId: id,
            deletePostbackTitle: title,
            deleteModalIsVisible: true
        }));
    }, []);

    const closeDeleteModal = useCallback(() => {
        setState(prevState => ({
            ...prevState,
            deleteModalIsVisible: false
        }));
    }, []);

    const getStatuses = useCallback((list) => {
        const hasStatuses = Array.isArray(list) ? list.length > 0 : !!list;
        if (!hasStatuses) {
            return staticNames.list?.all;
        }

        return <div className="postback__conversion">{getConversionStatuses(list)}</div>;
    }, [staticNames]);

    const getGoalActions = useCallback((ids) => {
        const hasGoalActions = Array.isArray(ids) ? ids.length > 0 : !!ids;
        if (!hasGoalActions) {
            return staticNames.list?.all;
        }

        return <TreeGoalActions data={getListGoalActionsByIds(ids)} />;
    }, [staticNames, getListGoalActionsByIds]);

    const goalActionsTrigger = propGoalActions;

    const getPostbacks = useMemo(() => {
        let postbacks = [];

        state.list.map((postback, i) => {
            postbacks.push({
                id: postback.id,
                title: escape(postback.config.title),
                url: postback.config.url,
                method: postback.config.method,
                conversion_statuses: getStatuses(postback.config.conversion_statuses),
                goal_action_ids: getGoalActions(postback.config.goal_action_ids),
                actions: (
                    <div className="row-controls">
                        <CustomTooltip id={`controlEdit-${i}`} tooltip={staticNamesActions.edit}>
                            <Link className="control-item view-button" to={getPath('editPostback', {id: postback.id})}>
                                <span className="fa fa-pencil-square-o"/><span
                                className={'link-title'}>{staticNamesActions.edit}</span>
                            </Link>
                        </CustomTooltip>
                        <CustomTooltip id={`controlDelete-${i}`} tooltip={staticNamesActions.delete}>
                            <a className="control-item delete-button" href="#!" onClick={() => {
                                showDeleteModal(postback.id, postback.config.title)
                            }}>
                                <span className="fa fa-trash-o"/><span
                                className={'link-title'}>{staticNamesActions.delete}</span>
                            </a>
                        </CustomTooltip>
                    </div>
                )
            });
        });

        return postbacks;
    }, [state.list, staticNamesActions, getStatuses, getGoalActions, showDeleteModal, goalActionsTrigger]);

    const maxCountPostbacks = runtimeConfig.maxCountPostbacks;

    return (
        <React.Fragment>
            {state.list.length === maxCountPostbacks
                ? <div className="postback__limit"><p className="text-warning">{staticNames.list?.limit}</p></div>
                : null
            }
            <div className="ibox-content">
                <div className={`postback__table ${isMobileDevice ? 'mobile-view' : ''}`}>
                    <CustomModal
                        visible={state.deleteModalIsVisible}
                        onClose={closeDeleteModal}
                        onConfirm={onDeletePostbackHandler}
                    >
                        <p>{staticNames.list?.deleteNoty} {'«' + state.deletePostbackTitle + '»'}?</p>
                    </CustomModal>
                    <DataComponent
                        onAction={onDataTableAction}
                        totalPages={state.totalPages}
                        headerNames={state.headerNames}
                        headerNamesArray={true}
                        autoTile={true}
                        data={getPostbacks}
                        loading={state.loading}
                        className="simple-table"
                        fields={[]}
                    />
                </div>
            </div>
        </React.Fragment>
    );
};

ListPostbacks.propTypes = {
    list: PropTypes.array,
    page: PropTypes.number,
    total: PropTypes.number,
    loading: PropTypes.bool,
    headerNames: PropTypes.array,
    goalActions: PropTypes.array,
    deleteModalIsVisible: PropTypes.bool,
    getListPostbacks: PropTypes.func,
    getListGoalActionsByIds: PropTypes.func,
    onDeletePostback: PropTypes.func,
    t: PropTypes.object
};

ListPostbacks.defaultProps = {
    list: [],
    page: 1,
    total: 0,
    loading: false,
    headerNames: [],
    goalActions: [],
    deleteModalIsVisible: false,
    getListPostbacks: () => {},
    getListGoalActionsByIds: () => {},
    onDeletePostback: () => {},
    t: {}
};

export default withTranslation(ListPostbacks);
